import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "../utils/api";
import { captureError } from "../utils/errorHandler";
import { clearOnboardingData, storeOnboardingData, storeOnboardingStep } from "../utils/onboardingRoutes";

// Async thunk to fetch dashboard data
export const fetchDashboard = createAsyncThunk(
  "dashboard/fetchDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get("/dashboard/");
      return response.data;
    } catch (error) {
      captureError(error, "fetching dashboard data");
      return rejectWithValue(error.message);
    }
  }
);

// Onboarding status lives in two places: this store (read by DashboardRedirect) and localStorage (read by
// getOnboardingRoute). Every dashboard payload goes through here so both always change together.
const applyDashboardPayload = (state, payload) => {
  state.hasFetched = true;
  state.data = payload;
  state.actionItems = payload?.dashboard_data?.action_items || [];
  state.onboardingCompleted = payload?.onboarding_completed ?? null;
  state.onboardingStep = payload?.onboarding_step ?? null;

  // Cache onboarding status in localStorage for instant redirects
  storeOnboardingData(payload);
};

const initialState = {
  data: null,
  actionItems: [],
  onboardingCompleted: null, // null = not checked yet, true/false = checked
  onboardingStep: null,
  isLoading: false,
  hasFetched: false, // Track if we've fetched at least once
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // Set action items manually (for fallback/static data)
    setActionItems(state, action) {
      state.actionItems = action.payload;
    },
    // A page fetched /dashboard/ itself; store it like fetchDashboard would. A copy is stored because the
    // store freezes what it holds and the page keeps using its own response object.
    dashboardSynced(state, action) {
      applyDashboardPayload(state, action.payload ? structuredClone(action.payload) : action.payload);
    },
    // The server confirmed a new onboarding step without a dashboard payload (e.g. step 1 completion).
    onboardingStepUpdated(state, action) {
      state.onboardingStep = action.payload;
      if (state.data) {
        state.data.onboarding_step = action.payload;
      }
      storeOnboardingStep(action.payload);
    },
    // Clear dashboard data (for logout)
    clearDashboard(state) {
      state.data = null;
      state.actionItems = [];
      state.onboardingCompleted = null;
      state.onboardingStep = null;
      state.isLoading = false;
      state.hasFetched = false;
      state.error = null;
      
      // Clear cached onboarding status from localStorage
      clearOnboardingData();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        applyDashboardPayload(state, action.payload);
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        state.error = action.payload;
      });
  },
});

export const { setActionItems, dashboardSynced, onboardingStepUpdated, clearDashboard } = dashboardSlice.actions;

export default dashboardSlice.reducer;

